import { useCallback, useEffect, useState } from 'react';
import * as adminApi from '../../api/adminApi';
import { useAuth } from '../../auth/AuthProvider';

const ORG_TYPES = ['originator', 'provider', 'custodian'];
const STATUSES = ['PENDING', 'ACTIVE', 'SUSPENDED'];

export function AdminOrganizationsPage() {
  const { session } = useAuth();
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: '',
    code: '',
    org_type: 'originator',
    contact_email: '',
    status: 'ACTIVE',
  });
  const [invite, setInvite] = useState({ orgId: '', email: '', role: 'admin' });
  const [lastInviteUrl, setLastInviteUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.listOrganizations({ page: 1, page_size: 100 });
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load organizations');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session?.isPlatformAdmin) {
      load();
    }
  }, [session?.isPlatformAdmin, load]);

  if (!session?.isPlatformAdmin) {
    return (
      <p className="text-sm text-eorig-muted">
        Platform admin access is required for the organization registry.
      </p>
    );
  }

  async function handleCreate(event) {
    event.preventDefault();
    setError(null);
    try {
      await adminApi.createOrganization({
        name: form.name,
        code: form.code,
        org_type: form.org_type,
        contact_email: form.contact_email || null,
        status: form.status,
      });
      setForm({
        name: '',
        code: '',
        org_type: 'originator',
        contact_email: '',
        status: 'ACTIVE',
      });
      await load();
    } catch (err) {
      setError(err.message || 'Create failed');
    }
  }

  async function handleStatus(org, status) {
    setError(null);
    try {
      await adminApi.updateOrganization(org.id, { status });
      await load();
    } catch (err) {
      setError(err.message || 'Update failed');
    }
  }

  async function handleInvite(event) {
    event.preventDefault();
    setError(null);
    setLastInviteUrl(null);
    try {
      const result = await adminApi.inviteOrgAdmin(invite.orgId, {
        email: invite.email,
        role: invite.role,
      });
      setLastInviteUrl(result.invite_url || null);
      setInvite({ orgId: invite.orgId, email: '', role: 'admin' });
    } catch (err) {
      setError(err.message || 'Invite failed');
    }
  }

  return (
    <div className="space-y-6">
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      <form
        onSubmit={handleCreate}
        className="grid gap-3 rounded-2xl border border-eorig-border bg-white p-4 sm:grid-cols-2"
      >
        <h2 className="sm:col-span-2 text-sm font-semibold">Create organization</h2>
        <input
          required
          placeholder="Name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        />
        <input
          required
          placeholder="Code"
          value={form.code}
          onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        />
        <select
          value={form.org_type}
          onChange={(e) => setForm((f) => ({ ...f, org_type: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        >
          {ORG_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={form.status}
          onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          type="email"
          placeholder="Contact email"
          value={form.contact_email}
          onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm sm:col-span-2"
        />
        <button
          type="submit"
          className="rounded-xl bg-umb-navy px-4 py-2 text-sm font-semibold text-white sm:col-span-2"
        >
          Create
        </button>
      </form>

      <form
        onSubmit={handleInvite}
        className="grid gap-3 rounded-2xl border border-eorig-border bg-white p-4 sm:grid-cols-2"
      >
        <h2 className="sm:col-span-2 text-sm font-semibold">Invite org admin</h2>
        <select
          required
          value={invite.orgId}
          onChange={(e) => setInvite((i) => ({ ...i, orgId: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        >
          <option value="">Select organization</option>
          {items.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name} ({org.org_type})
            </option>
          ))}
        </select>
        <input
          required
          type="email"
          placeholder="Admin email"
          value={invite.email}
          onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        />
        <button
          type="submit"
          className="rounded-xl bg-umb-navy px-4 py-2 text-sm font-semibold text-white sm:col-span-2"
        >
          Create invite link
        </button>
        {lastInviteUrl ? (
          <p className="sm:col-span-2 break-all text-xs text-eorig-muted">
            Invite URL: {lastInviteUrl}
          </p>
        ) : null}
      </form>

      <div className="overflow-hidden rounded-2xl border border-eorig-border bg-white">
        {loading ? (
          <p className="p-4 text-sm text-eorig-muted">Loading…</p>
        ) : items.length === 0 ? (
          <p className="p-4 text-sm text-eorig-muted">No organizations yet.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-eorig-border bg-eorig-surface text-xs uppercase text-eorig-muted">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((org) => (
                <tr key={org.id} className="border-b border-eorig-border/70">
                  <td className="px-4 py-3">
                    <div className="font-medium">{org.name}</div>
                    <div className="text-xs text-eorig-muted">{org.code}</div>
                  </td>
                  <td className="px-4 py-3 capitalize">{org.org_type}</td>
                  <td className="px-4 py-3">{org.status}</td>
                  <td className="px-4 py-3 space-x-2">
                    {org.status !== 'ACTIVE' ? (
                      <button
                        type="button"
                        className="text-xs font-semibold text-eorig-cyan"
                        onClick={() => handleStatus(org, 'ACTIVE')}
                      >
                        Activate
                      </button>
                    ) : null}
                    {org.status !== 'SUSPENDED' ? (
                      <button
                        type="button"
                        className="text-xs font-semibold text-red-600"
                        onClick={() => handleStatus(org, 'SUSPENDED')}
                      >
                        Suspend
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
